import { Component } from '@theme/component';
import { ZoomMediaSelectedEvent } from '@theme/events';
import { DialogCloseEvent } from '@theme/dialog';

export class ZoomDialog extends Component {
  requiredRefs = ['dialog', 'media', 'thumbnails'];
  #activeIndex = 0;
  #opener = null;
  #loaded = new Set();

  #desktopZoomed = false;
  #desktopX = 0;
  #desktopY = 0;
  #drag = null;
  #mobileScale = 1;
  #mobileX = 0;
  #mobileY = 0;
  #gesture = null;
  #ignoreClickUntil = 0;
  #lastTap = 0;
  preventMobileBrowserZoom = (event) => {
    if (matchMedia('(max-width: 749px)').matches) event.preventDefault();
  };

  resetMobileZoom = () => {
    this.#desktopZoomed = false;
    this.#desktopX = this.#desktopY = 0;
    this.#drag = null;
    this.#mobileScale = 1;
    this.#mobileX = 0;
    this.#mobileY = 0;
    this.#gesture = null;
    this.#lastTap = 0;
    for (const item of this.refs.media) {
      const image = item.querySelector('.lfs-zoom-image-wrapper img');
      image?.style.removeProperty('transform');
      image?.style.removeProperty('width');
      image?.style.removeProperty('height');
      image?.style.removeProperty('flex');
      image?.style.removeProperty('max-width');
      image?.style.removeProperty('max-height');
      image?.parentElement?.style.removeProperty('width');
      image?.parentElement?.style.removeProperty('height');
      image?.parentElement?.style.removeProperty('flex');
      if (image) image.style.cursor = matchMedia('(min-width: 750px)').matches ? 'zoom-in' : '';
    }
    this.fitMobileImage();
  };

  fitMobileImage() {
    if (!matchMedia('(max-width: 749px)').matches || !this.refs.dialog.open) return;
    const image = this.refs.media[this.#activeIndex]?.querySelector('.lfs-zoom-image-wrapper img');
    const stage = this.refs.dialog.querySelector('.dialog-zoomed-gallery');
    if (!image || !stage) return;
    const ratio = Number(image.getAttribute('width')) / Number(image.getAttribute('height'));
    if (!(ratio > 0) || !stage.clientHeight) return;
    // Start from viewport width, never from a full-height cover crop.
    const width = Math.min(this.refs.dialog.clientWidth, stage.clientHeight * ratio);
    image.style.setProperty('width', width + 'px', 'important');
    image.style.setProperty('height', width / ratio + 'px', 'important');
  }

  applyMobileZoom() {
    const image = this.refs.media[this.#activeIndex]?.querySelector('.lfs-zoom-image-wrapper img');
    if (!image) return;
    const maxX = image.offsetWidth * (this.#mobileScale - 1) / 2;
    const maxY = image.offsetHeight * (this.#mobileScale - 1) / 2;
    this.#mobileX = Math.max(-maxX, Math.min(maxX, this.#mobileX));
    this.#mobileY = Math.max(-maxY, Math.min(maxY, this.#mobileY));
    image.style.transform = 'translate(' + this.#mobileX + 'px, ' + this.#mobileY + 'px) scale(' + this.#mobileScale + ')';
  }

  handleTouchStart = (event) => {
    if (!matchMedia('(max-width: 749px)').matches) return;
    if (event.touches.length > 1) event.preventDefault();
    if (!event.target.closest('.lfs-zoom-image-wrapper')) return;
    const touches = event.touches;
    if (touches.length > 1) {
      event.preventDefault();
      this.#gesture = {
        distance: Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY),
        scale: this.#mobileScale,
        moved: true
      };
      this.#lastTap = 0;
    } else {
      this.#gesture = { x: touches[0].clientX, y: touches[0].clientY,
        startX: this.#mobileX, startY: this.#mobileY, moved: false };
    }
  };

  handleTouchMove = (event) => {
    if (!matchMedia('(max-width: 749px)').matches) return;
    if (event.touches.length > 1) event.preventDefault();
    if (!this.#gesture) return;
    event.preventDefault();
    const touches = event.touches;
    if (touches.length > 1 && this.#gesture.distance) {
      const distance = Math.hypot(touches[0].clientX - touches[1].clientX, touches[0].clientY - touches[1].clientY);
      this.#mobileScale = Math.max(1, Math.min(2, this.#gesture.scale * distance / this.#gesture.distance));
      this.#gesture.moved = true;
    } else if (touches.length === 1 && this.#gesture.x !== undefined) {
      const dx = touches[0].clientX - this.#gesture.x;
      const dy = touches[0].clientY - this.#gesture.y;
      if (Math.hypot(dx, dy) > 5) this.#gesture.moved = true;
      if (this.#mobileScale > 1) {
        this.#mobileX = this.#gesture.startX + dx;
        this.#mobileY = this.#gesture.startY + dy;
      }
    }
    this.applyMobileZoom();
  };

  handleTouchEnd = (event) => {
    if (!this.#gesture) return;
    if (this.#gesture.moved) {
      this.#ignoreClickUntil = Date.now() + 500;
      this.#lastTap = 0;
    } else if (event.touches.length === 0) {
      const now = Date.now();
      if (now - this.#lastTap < 300) {
        event.preventDefault();
        this.#mobileScale = this.#mobileScale > 1 ? 1 : 2;
        this.#mobileX = this.#mobileY = 0;
        this.applyMobileZoom();
        this.#ignoreClickUntil = now + 500;
        this.#lastTap = 0;
      } else {
        this.#lastTap = now;
      }
    }
    this.#gesture = null;
  };


  connectedCallback() {
    super.connectedCallback();
    this.refs.dialog.addEventListener('touchstart', this.handleTouchStart, { passive: false });
    this.refs.dialog.addEventListener('touchmove', this.handleTouchMove, { passive: false });
    this.refs.dialog.addEventListener('touchend', this.handleTouchEnd, { passive: false });
    this.refs.dialog.addEventListener('touchcancel', this.resetMobileZoom);
    window.addEventListener('resize', this.resetMobileZoom);
    this.refs.dialog.addEventListener('gesturestart', this.preventMobileBrowserZoom, { passive: false });
    this.refs.dialog.addEventListener('gesturechange', this.preventMobileBrowserZoom, { passive: false });
    this.refs.dialog.addEventListener('mousedown', this.handleMouseDown);
    window.addEventListener('mousemove', this.handleMouseMove);
    window.addEventListener('mouseup', this.handleMouseUp);
    window.addEventListener('blur', this.handleMouseUp);
    this.refs.dialog.addEventListener('click', this.handleBackdropClick);
    this.refs.dialog.addEventListener('wheel', this.handleWheel, { passive: false });
    this.refs.dialog.addEventListener('cancel', this.handleCancel);
  }

  disconnectedCallback() {
    this.refs.dialog.removeEventListener('touchstart', this.handleTouchStart);
    this.refs.dialog.removeEventListener('touchmove', this.handleTouchMove);
    this.refs.dialog.removeEventListener('touchend', this.handleTouchEnd);
    this.refs.dialog.removeEventListener('touchcancel', this.resetMobileZoom);
    window.removeEventListener('resize', this.resetMobileZoom);
    this.refs.dialog.removeEventListener('gesturestart', this.preventMobileBrowserZoom);
    this.refs.dialog.removeEventListener('gesturechange', this.preventMobileBrowserZoom);
    this.refs.dialog.removeEventListener('mousedown', this.handleMouseDown);
    window.removeEventListener('mousemove', this.handleMouseMove);
    window.removeEventListener('mouseup', this.handleMouseUp);
    window.removeEventListener('blur', this.handleMouseUp);
    this.refs.dialog.removeEventListener('click', this.handleBackdropClick);
    this.refs.dialog.removeEventListener('wheel', this.handleWheel);
    this.refs.dialog.removeEventListener('cancel', this.handleCancel);
    super.disconnectedCallback();
  }

  open(index, event) {
    event?.preventDefault();
    this.#opener = document.activeElement;
    this.selectThumbnail(Number(index));
    if (!this.refs.dialog.open) this.refs.dialog.showModal();
    this.fitMobileImage();
    this.refs.dialog.querySelector('.dialog-zoomed-gallery__close-button')?.focus({ preventScroll: true });
  }

  close() {
    this.closeDialog();
  }

  closeDialog() {
    if (!this.refs.dialog.open) return;
    this.pauseMedia();
    this.resetMobileZoom();
    this.refs.dialog.close();
    window.dispatchEvent(new DialogCloseEvent());
    if (this.#opener instanceof HTMLElement && this.#opener.isConnected) {
      this.#opener.focus({ preventScroll: true });
    }
  }

  handleCancel = (event) => {
    event.preventDefault();
    this.close();
  };


  applyDesktopZoom() {
    const image = this.refs.media[this.#activeIndex]?.querySelector('.lfs-zoom-image-wrapper img');
    if (!image) return;
    const frame = image.parentElement;
    if (this.#desktopZoomed && !frame.style.width) {
      const width = image.offsetWidth;
      const height = image.offsetHeight;
      // Clip the enlarged image inside its original, centered frame.
      frame.style.flex = '0 0 auto';
      frame.style.width = width + 'px';
      frame.style.height = height + 'px';
      image.style.width = width + 'px';
      image.style.height = height + 'px';
      image.style.flex = '0 0 auto';
      image.style.maxWidth = 'none';
      image.style.maxHeight = 'none';
    } else if (!this.#desktopZoomed) {
      for (const property of ['width', 'height', 'flex', 'max-width', 'max-height']) image.style.removeProperty(property);
      frame.style.removeProperty('width');
      frame.style.removeProperty('height');
      frame.style.removeProperty('flex');
    }
    const maxX = image.offsetWidth / 2;
    const maxY = image.offsetHeight / 2;
    this.#desktopX = Math.max(-maxX, Math.min(maxX, this.#desktopX));
    this.#desktopY = Math.max(-maxY, Math.min(maxY, this.#desktopY));
    image.style.transform = this.#desktopZoomed
      ? 'translate(' + this.#desktopX + 'px, ' + this.#desktopY + 'px) scale(2)' : '';
    image.style.cursor = this.#desktopZoomed ? (this.#drag ? 'grabbing' : 'grab') : 'zoom-in';
  }

  handleMouseDown = (event) => {
    if (event.button !== 0 || !this.#desktopZoomed || !matchMedia('(min-width: 750px)').matches) return;
    if (!(event.target instanceof Element) || !event.target.closest('.lfs-zoom-image-wrapper img')) return;
    event.preventDefault();
    this.#drag = { x: event.clientX, y: event.clientY, startX: this.#desktopX, startY: this.#desktopY, moved: false };
    this.applyDesktopZoom();
  };

  handleMouseMove = (event) => {
    if (!this.#drag) return;
    const dx = event.clientX - this.#drag.x;
    const dy = event.clientY - this.#drag.y;
    if (Math.hypot(dx, dy) > 5) this.#drag.moved = true;
    if (!this.#drag.moved) return;
    event.preventDefault();
    this.#desktopX = this.#drag.startX + dx;
    this.#desktopY = this.#drag.startY + dy;
    this.applyDesktopZoom();
  };

  handleMouseUp = () => {
    if (!this.#drag) return;
    // Suppress the click generated by releasing a drag; a normal click still toggles zoom.
    if (this.#drag.moved) this.#ignoreClickUntil = Date.now() + 250;
    this.#drag = null;
    this.applyDesktopZoom();
  };

  handleWheel = (event) => {
    if (event.ctrlKey) return;
    event.preventDefault();
    if (!this.#desktopZoomed || !matchMedia('(min-width: 750px)').matches) return;
    const unit = event.deltaMode === 1 ? 16 : event.deltaMode === 2 ? this.refs.dialog.clientHeight : 1;
    this.#desktopX -= (event.shiftKey && !event.deltaX ? event.deltaY : event.deltaX) * unit;
    this.#desktopY -= (event.shiftKey && !event.deltaX ? 0 : event.deltaY) * unit;
    this.applyDesktopZoom();
  };

  handleBackdropClick = (event) => {
    if (Date.now() < this.#ignoreClickUntil) return;
    const target = event.target;
    if (!(target instanceof Element)) return;
    if (target.closest('button, video, iframe, model-viewer, deferred-media')) return;
    const image = this.refs.media[this.#activeIndex]?.querySelector('img');
    if (image) {
      const rect = this.#desktopZoomed ? image.parentElement.getBoundingClientRect() : image.getBoundingClientRect();
      if (event.clientX >= rect.left && event.clientX <= rect.right &&
          event.clientY >= rect.top && event.clientY <= rect.bottom) {
        if (matchMedia('(min-width: 750px)').matches) {
          this.#desktopZoomed = !this.#desktopZoomed;
          this.#desktopX = this.#desktopY = 0;
          this.applyDesktopZoom();
        }
        return;
      }
    }
    this.close();
  };

  handleKeyDown(event) {
    if (event.key === 'Escape') {
      event.preventDefault();
      this.close();
    } else if (event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
      event.preventDefault();
      event.key === 'ArrowLeft' ? this.previous() : this.next();
    }
  }

  previous() {
    this.selectThumbnail((this.#activeIndex - 1 + this.refs.media.length) % this.refs.media.length);
  }

  next() {
    this.selectThumbnail((this.#activeIndex + 1) % this.refs.media.length);
  }

  handleThumbnailClick(index) {
    this.selectThumbnail(Number(index));
  }

  handleThumbnailPointerEnter(index) {
    const media = this.refs.media[Number(index)];
    if (media) this.loadHighResolutionImage(media);
  }

  pauseMedia() {
    const current = this.refs.media[this.#activeIndex];
    current?.querySelectorAll('video').forEach((video) => video.pause());
    current?.querySelectorAll('deferred-media, product-model').forEach((element) => element.pause?.());
  }

  selectThumbnail(index) {
    const { media, thumbnails } = this.refs;
    if (!Number.isInteger(index) || !media[index]) return;
    this.pauseMedia();
    this.resetMobileZoom();
    this.#activeIndex = index;
    media.forEach((item, i) => {
      item.hidden = i !== index;
      item.setAttribute('aria-hidden', String(i !== index));
    });
    thumbnails.querySelectorAll('button').forEach((button, i) => {
      button.setAttribute('aria-selected', String(i === index));
    });
    this.fitMobileImage();
    this.loadHighResolutionImage(media[index]);
    this.dispatchEvent(new ZoomMediaSelectedEvent(index));
  }

  loadHighResolutionImage(mediaContainer) {
    const image = mediaContainer.querySelector('img.product-media__image');
    if (!(image instanceof HTMLImageElement)) return;
    image.loading = 'eager';
    const url = image.getAttribute('data_max_resolution');
    if (!url || this.#loaded.has(url)) return;
    this.#loaded.add(url);
    const highResolution = new Image();
    highResolution.onload = () => {
      image.removeAttribute('srcset');
      image.src = url;
      this.fitMobileImage();
    };
    highResolution.onerror = () => this.#loaded.delete(url);
    highResolution.src = url;
  }
}

if (!customElements.get('zoom-dialog')) {
  customElements.define('zoom-dialog', ZoomDialog);
}
